/**
 * Tutor moves of a séance (motif, old/new slot) on the session detail panel.
 */
import * as React from 'react';
import type { SessionReschedule, SessionRescheduleSlot } from '@/types/remote/session.types';
import { parseLocalDateString } from '@/utils/format-date-utils';

/** `05/11/2026 10h00–11h00` */
export function formatRescheduleSlot(slot: SessionRescheduleSlot): string {
  const date = parseLocalDateString(slot.date).toLocaleDateString('fr-FR');
  return `${date} ${slot.startTime.replace(':', 'h')}–${slot.endTime.replace(':', 'h')}`;
}

/** Tutor full name, or null when the author account is gone. */
export function formatRescheduleAuthor(reschedule: SessionReschedule): string | null {
  const name = [reschedule.movedBy?.firstName, reschedule.movedBy?.lastName]
    .filter(Boolean)
    .join(' ');
  return name || null;
}

/** Props for {@link SessionRescheduleBanner}. */
interface SessionRescheduleBannerProps {
  reschedules?: SessionReschedule[] | null;
}

/** « Séance déplacée » box for the latest move; renders nothing for a séance never moved. */
export const SessionRescheduleBanner: React.FC<SessionRescheduleBannerProps> = ({
  reschedules,
}) => {
  const latest = reschedules?.[0];
  if (!latest) return null;
  const author = formatRescheduleAuthor(latest);
  const count = reschedules.length;

  return (
    <div
      className="rounded-lg p-4 border space-y-2"
      style={{
        background: 'color-mix(in srgb, rgb(var(--status-warning)) 8%, transparent)',
        borderColor: 'rgb(var(--status-warning))',
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold" style={{ color: 'rgb(var(--status-warning))' }}>
          Séance déplacée
        </span>
        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          — {new Date(latest.movedAt).toLocaleString('fr-FR')}
          {author ? ` par ${author}` : ''}
        </span>
        {count > 1 && (
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            ({count} déplacements, voir l'historique)
          </span>
        )}
      </div>
      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
        Du {formatRescheduleSlot(latest.oldSlot)} au {formatRescheduleSlot(latest.newSlot)}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          Motif :
        </span>
        <span className="text-sm italic" style={{ color: 'var(--color-1-nuance-1)' }}>
          "{latest.reason}"
        </span>
      </div>
    </div>
  );
};

/** Props for {@link SessionRescheduleTimelineItems}. */
interface SessionRescheduleTimelineItemsProps {
  reschedules?: SessionReschedule[] | null;
}

/** One « Séance déplacée » entry per move, oldest first like the rest of the Historique timeline. */
export const SessionRescheduleTimelineItems: React.FC<SessionRescheduleTimelineItemsProps> = ({
  reschedules,
}) => (
  <>
    {[...(reschedules ?? [])].reverse().map((reschedule) => {
      const author = formatRescheduleAuthor(reschedule);
      return (
        <div key={reschedule.id} className="relative">
          <div className="absolute -left-6 w-4 h-4 rounded-full bg-orange-500" />
          <div className="text-xs text-gray-500">
            {new Date(reschedule.movedAt).toLocaleString('fr-FR')}
          </div>
          <div className="text-sm font-medium">Séance déplacée</div>
          {author && <div className="text-xs text-gray-600">Par {author}</div>}
          <div className="text-xs text-gray-600 mt-1">
            Du {formatRescheduleSlot(reschedule.oldSlot)} au{' '}
            {formatRescheduleSlot(reschedule.newSlot)}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <span className="text-xs text-gray-500">Motif :</span>
            <span className="text-xs italic text-gray-700">"{reschedule.reason}"</span>
          </div>
        </div>
      );
    })}
  </>
);
